import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ViewerImage {
  /** What it is, in words: "ID front". */
  name: string;
  url: string;
}

/**
 * Pictures in a window over the page, never in a tab of their own: the one
 * that was pressed, large, with Previous and Next when there are several.
 * Closing returns to the page exactly as it was.
 */
export function ImageViewer({
  images,
  index,
  onIndexChange,
  onClose,
  note,
}: Readonly<{
  images: ViewerImage[];
  /** Which picture is shown; null when the viewer is closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** A line under the picture, such as how long the links last. */
  note?: string;
}>) {
  const image = index === null ? undefined : images[index];
  const several = images.length > 1;
  const step = (by: 1 | -1) => {
    if (index === null) return;
    onIndexChange((index + by + images.length) % images.length);
  };
  return (
    <Dialog
      open={image !== undefined}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{image?.name ?? ''}</DialogTitle>
          <DialogDescription>
            {several && index !== null
              ? `${index + 1} of ${images.length}. `
              : ''}
            {note ?? ''}
          </DialogDescription>
        </DialogHeader>
        {image ? (
          <div className="flex items-center justify-center rounded-lg bg-surface-2 p-2">
            <img
              src={image.url}
              alt={image.name}
              className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
            />
          </div>
        ) : null}
        {several ? (
          <div className="flex justify-between gap-2 pt-3">
            <Button variant="outline" onClick={() => step(-1)}>
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
              Previous
            </Button>
            <Button variant="outline" onClick={() => step(1)}>
              Next
              <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
